import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchMatchComparison } from '../lib/api';
import { formatDate, formatPrice } from '../lib/format';
import type { MatchComparison, ComparisonGroup as CG } from '../lib/types';
import ComparisonGroup from '../components/comparison/ComparisonGroup';
import AllTicketsTable from '../components/comparison/AllTicketsTable';
import EmptyState from '../components/ui/EmptyState';
import { MatchHeroSkeleton, ComparisonGroupSkeleton } from '../components/ui/Skeleton';
import usePageTitle from '../hooks/usePageTitle';
import {
  ChevronRightIcon,
  CalendarIcon,
  TrophyIcon,
  ScaleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

const TICKETED_NAME = 'Ticketed';

interface TicketedStats {
  cheaperCount: number;
  moreExpensiveCount: number;
  sameCount: number;
  notPresentCount: number;
  totalGroups: number;
  totalSaved: number;       // sum of savings in categories where Ticketed is cheapest
  totalOverpay: number;     // sum of extra cost in categories where Ticketed is most expensive
}

function computeTicketedStats(comparisons: CG[]): TicketedStats {
  let cheaperCount = 0;
  let moreExpensiveCount = 0;
  let sameCount = 0;
  let notPresentCount = 0;
  let totalSaved = 0;
  let totalOverpay = 0;

  for (const group of comparisons) {
    const ticketed = group.tickets.find((t) => t.supplier.name === TICKETED_NAME);
    if (!ticketed || ticketed.price === null) {
      notPresentCount++;
      continue;
    }

    const competitorPrices = group.tickets
      .filter((t) => t.supplier.name !== TICKETED_NAME && t.price !== null)
      .map((t) => t.price as number);

    if (competitorPrices.length === 0) {
      notPresentCount++;
      continue;
    }

    const cheapestCompetitor = Math.min(...competitorPrices);

    if (ticketed.price < cheapestCompetitor) {
      cheaperCount++;
      totalSaved += cheapestCompetitor - ticketed.price;
    } else if (ticketed.price > cheapestCompetitor) {
      moreExpensiveCount++;
      totalOverpay += ticketed.price - cheapestCompetitor;
    } else {
      sameCount++;
    }
  }

  return {
    cheaperCount,
    moreExpensiveCount,
    sameCount,
    notPresentCount,
    totalGroups: comparisons.length,
    totalSaved,
    totalOverpay,
  };
}

export default function MatchPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<MatchComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(
    data?.match
      ? `${data.match.home_team} v ${data.match.away_team}`
      : undefined
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchMatchComparison(parseInt(id))
      .then(setData)
      .catch(() => setError('Failed to load match data'))
      .finally(() => setLoading(false));
  }, [id]);

  const comparisons = useMemo(() => data?.comparisons || [], [data]);
  const stats = useMemo(() => computeTicketedStats(comparisons), [comparisons]);
  const hasTicketedInAnyGroup = stats.totalGroups - stats.notPresentCount > 0;

  if (loading) {
    return (
      <div>
        <div className="h-4 w-48 mb-6" />
        <MatchHeroSkeleton />
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <ComparisonGroupSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data?.match) {
    return (
      <EmptyState
        icon={<ScaleIcon className="w-7 h-7" />}
        title="Match not found"
        description={error || "We couldn't find the match you're looking for."}
        actionLabel="Back to Fixtures"
        actionTo="/"
      />
    );
  }

  const { match, ungrouped } = data;

  return (
    <div>
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-sm text-slate-400 mb-6 overflow-x-auto">
        <Link to="/" className="hover:text-slate-200 transition-colors shrink-0">Home</Link>
        <ChevronRightIcon className="w-3.5 h-3.5 shrink-0" />
        {match.league_name && match.league_slug && (
          <>
            <Link
              to={`/league/${match.league_slug}`}
              className="hover:text-slate-200 transition-colors shrink-0"
            >
              {match.league_name}
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 shrink-0" />
          </>
        )}
        <span className="text-slate-200 truncate">
          {match.home_team} v {match.away_team}
        </span>
      </nav>

      {/* Match hero card */}
      <div className="card p-6 sm:p-8 mb-8">
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
          {/* Home team */}
          <Link to={`/team/${match.home_slug}`} className="flex flex-col items-center gap-2 flex-1 group">
            <img
              src={match.home_logo}
              alt={match.home_team}
              className="w-16 h-16 sm:w-20 sm:h-20 object-contain group-hover:scale-105 transition-transform"
            />
            <h2 className="text-lg font-semibold text-slate-100 text-center group-hover:text-emerald-400 transition-colors">
              {match.home_team}
            </h2>
          </Link>

          {/* VS divider */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-2xl font-bold text-slate-600">vs</span>
          </div>

          {/* Away team */}
          <Link to={`/team/${match.away_slug}`} className="flex flex-col items-center gap-2 flex-1 group">
            <img
              src={match.away_logo}
              alt={match.away_team}
              className="w-16 h-16 sm:w-20 sm:h-20 object-contain group-hover:scale-105 transition-transform"
            />
            <h2 className="text-lg font-semibold text-slate-100 text-center group-hover:text-emerald-400 transition-colors">
              {match.away_team}
            </h2>
          </Link>
        </div>

        {/* Date & league info */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-6 text-sm text-slate-400">
          <span className="flex items-center gap-1.5">
            <CalendarIcon className="w-4 h-4" />
            {formatDate(match.match_date)}
          </span>
          {match.league_name && (
            <Link
              to={`/league/${match.league_slug}`}
              className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors"
            >
              <TrophyIcon className="w-4 h-4" />
              {match.league_name}
            </Link>
          )}
        </div>
      </div>

      {/* Ticketed summary stats */}
      {comparisons.length > 0 && hasTicketedInAnyGroup && (
        <div className="card p-4 sm:p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-400">Ticketed</span>
            <span className="text-xs text-slate-500">price position across matched categories</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Cheaper */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/8 border border-emerald-500/15">
              <CheckCircleIcon className="w-5 h-5 text-emerald-400 shrink-0" />
              <div>
                <p className="text-lg font-bold text-emerald-400">{stats.cheaperCount}</p>
                <p className="text-[11px] text-emerald-400/70">Cheaper</p>
              </div>
            </div>
            {/* More expensive */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-rose-500/8 border border-rose-500/15">
              <ArrowTrendingUpIcon className="w-5 h-5 text-rose-400 shrink-0" />
              <div>
                <p className="text-lg font-bold text-rose-400">{stats.moreExpensiveCount}</p>
                <p className="text-[11px] text-rose-400/70">More expensive</p>
              </div>
            </div>
            {/* Same price */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 border border-slate-700/40">
              <MinusCircleIcon className="w-5 h-5 text-slate-400 shrink-0" />
              <div>
                <p className="text-lg font-bold text-slate-300">{stats.sameCount}</p>
                <p className="text-[11px] text-slate-500">Same price</p>
              </div>
            </div>
            {/* Avg saving / overpay */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 border border-slate-700/40">
              <ScaleIcon className="w-5 h-5 text-slate-400 shrink-0" />
              <div>
                {stats.totalSaved > 0 ? (
                  <>
                    <p className="text-lg font-bold text-emerald-400">{formatPrice(stats.totalSaved)}</p>
                    <p className="text-[11px] text-emerald-400/70">Total saved</p>
                  </>
                ) : stats.totalOverpay > 0 ? (
                  <>
                    <p className="text-lg font-bold text-rose-400">+{formatPrice(stats.totalOverpay)}</p>
                    <p className="text-[11px] text-rose-400/70">Above cheapest</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-slate-300">$0</p>
                    <p className="text-[11px] text-slate-500">Difference</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Comparisons */}
      {comparisons.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-100">
              Price Comparisons
            </h2>
            <span className="text-xs text-slate-500 bg-slate-800 px-2.5 py-1 rounded-full border border-slate-700/50">
              {comparisons.length} categor{comparisons.length !== 1 ? 'ies' : 'y'} matched
            </span>
          </div>

          <div className="grid gap-4">
            {comparisons.map((group, i) => (
              <ComparisonGroup key={i} group={group} />
            ))}
          </div>
        </section>
      )}

      {comparisons.length === 0 && (
        <section className="mb-8">
          <EmptyState
            icon={<ScaleIcon className="w-7 h-7" />}
            title="No price comparisons yet"
            description="Cross-supplier price comparisons aren't available for this match yet. Check back closer to the match date."
          />
        </section>
      )}

      {/* Ungrouped tickets */}
      {ungrouped && ungrouped.length > 0 && (
        <section>
          <AllTicketsTable tickets={ungrouped} />
        </section>
      )}
    </div>
  );
}
